import type { EntryRow } from '../../shared/types';
import { formatDuration } from '../lib/duration';
import { formatDayMonth, formatINR, formatWeekday } from '../lib/format';
import { WorkTypeBadge } from './WorkTypeBadge';

/**
 * The small-screen counterpart to the table. Editing happens in a sheet rather
 * than in place, so nothing here has to fit into a scrolling row.
 */
export function EntryCards({
  entries,
  showAmounts,
  onEdit,
  onDelete,
}: {
  entries: EntryRow[];
  showAmounts: boolean;
  onEdit: (entry: EntryRow) => void;
  onDelete: (entry: EntryRow) => void;
}) {
  return (
    <ul className="space-y-2.5">
      {entries.map((entry) => (
        <li
          key={entry.id}
          className="rounded-[var(--radius-card)] border border-[var(--color-cyan-200)] bg-[var(--color-cyan-50)] px-4 py-3"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="nums text-[12px] text-[var(--color-ink-muted)]">
                {formatDayMonth(entry.work_date)} · {formatWeekday(entry.work_date)}
              </p>
              <p className="mt-0.5 font-semibold">{entry.title}</p>
              <p className="mt-1 flex items-center gap-1.5 text-[12px] text-[var(--color-ink-soft)]">
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: entry.project_color }}
                  aria-hidden
                />
                {entry.project_name}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p className="nums font-display text-[18px] font-semibold">{formatDuration(entry.minutes)}</p>
              {showAmounts && (
                <p className="nums text-[12px] text-[var(--color-ink-muted)]">{formatINR(entry.amount_inr)}</p>
              )}
            </div>
          </div>

          {entry.remark && (
            <p className="mt-2 text-[12px] leading-relaxed text-[var(--color-ink-soft)]">{entry.remark}</p>
          )}

          <div className="mt-2.5 flex items-center gap-2 border-t border-[var(--color-cyan-200)] pt-2.5">
            <WorkTypeBadge type={entry.work_type} />
            <div className="ml-auto flex gap-1">
              <button type="button" className="btn btn-ghost !py-1 text-[12px]" onClick={() => onEdit(entry)}>
                Edit
              </button>
              <button
                type="button"
                className="btn btn-ghost !py-1 text-[12px] !text-[var(--color-coral-600)]"
                onClick={() => {
                  if (confirm(`Delete "${entry.title}"? This cannot be undone.`)) onDelete(entry);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
